
import {
  defineComponent, PropType, toRef, computed, ref
} from 'vue';
import Triangle from '@/components/svg-components/Triangle.vue';
import MinusCircle from '@/components/svg/minus-circle.svg?inline';
import IsFavorite from '@/components/svg/is-favorite.svg?inline';
import FavouriteAddSimple from '@/components/svg/favourite-add-simple.svg?inline';
import FavouriteRemove from '@/components/svg/favourite-remove.svg?inline';
import IsNotFavorite from '@/components/svg/is-not-favorite.svg?inline';
import { ExternalTagAssignment, UserVoted } from '@/services/TaggingService';
import { PostSubscription, Subscription } from '@/views/subscriptions/models/Subscription';
import { useStore } from 'vue2-helpers/vuex';
import BCXPopover from '@/components/molecules/BCXPopover.vue';
import useI18n from '@/mixins/useI18n';
import { Tag } from '@/models/Tags';

export default defineComponent({
  components: {
    BCXPopover,
    Triangle,
    MinusCircle,
    IsFavorite,
    IsNotFavorite,
    FavouriteAddSimple,
    FavouriteRemove
  },
  props: {
    tag: {
      type: Object as PropType<ExternalTagAssignment | Tag>,
      required: true
    },
    disableVoting: {
      type: Boolean,
      default: false
    },
    isRemoveable: {
      type: Boolean,
      default: false
    },
    showVoteResult: {
      type: Boolean,
      default: true
    },
    showSubscribeButton: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const { t } = useI18n();
    const refTag = toRef(props, 'tag');
    const store = useStore();
    const subscribedTag = computed<Subscription | undefined>(() => store.state.subscriptions.subscriptionList.find((subscription: Subscription) => props.tag.tagId === subscription.subscribedObjectIdentifier));
    const showPopover = ref(false);
    let timer: ReturnType<typeof setTimeout>;
    // Temporary optimistic state: null = inactive, true = subscribed, false = unsubscribed
    const isSubscribedOptimistic = ref<boolean | null>(null);

    function setPopoverTimeout() {
      showPopover.value = true;
      clearTimeout(timer);

      timer = setTimeout(() => {
        showPopover.value = false;
      }, 1000);
    }

    // Use optimistic state for immediate UI feedback, fallback to real store value
    const isTagSubscribed = computed(() => {
      if (isSubscribedOptimistic.value !== null) return isSubscribedOptimistic.value;
      return !!subscribedTag.value;
    });

    async function toggleFavorite(tag: ExternalTagAssignment) {
      if (!props.showSubscribeButton) return;
      // Save current real state before any changes
      const wasAlreadySubscribed = !!subscribedTag.value;

      // Immediately update UI without waiting for the request
      isSubscribedOptimistic.value = !wasAlreadySubscribed;
      setPopoverTimeout();

      try {
        if (wasAlreadySubscribed) {
          // Remove subscription if user was already subscribed
          await store.dispatch('subscriptions/deleteSubscription', subscribedTag.value.subscriptionId);
        } else {
          // Add subscription if user was not subscribed
          const payload: PostSubscription = { businessObjectId: tag.tagId, subscriptionType: 'TAG' };
          await store.dispatch('subscriptions/postSubscription', payload);
        }
      } catch {
        // Revert UI to previous state if request failed
        isSubscribedOptimistic.value = wasAlreadySubscribed;
      } finally {
        // Reset optimistic state so the store value takes over again
        isSubscribedOptimistic.value = null;
      }
    }

    return {
      UserVoted, refTag, toggleFavorite, t, showPopover, isTagSubscribed
    };
  }
});
